// ./Navbar.js
import React, { useMemo, useState } from "react";
import {
  Bars3Icon,
  XMarkIcon,
  ChevronDownIcon,
  GlobeAltIcon,
  UserCircleIcon,
  ChevronRightIcon,
  ShoppingCartIcon,
} from "@heroicons/react/24/outline";
import AnanasLogo from "../images/AnanasSolutionsLogo.webp";
import { createPortal } from "react-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { CheckIcon } from "@heroicons/react/24/solid";
import { useNavigate } from "react-router-dom";

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [submenu, setSubmenu] = useState(null);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const navigate = useNavigate();

  const { t, i18n } = useTranslation();
  const { currentUser, roles, logout, permissions } = useAuth();

  const hasManagementAccess = (permissions || []).some(
    (p) => typeof p.code === "string" && p.code.startsWith("management.")
  );

  const LANGUAGES = [
    { code: "fr", label: "Français" },
    { code: "en", label: "English" },
  ];
  const currentLang = (i18n.resolvedLanguage || i18n.language || "fr").slice(
    0,
    2
  );

  const setLang = (code) => {
    const short = (code || "").slice(0, 2).toLowerCase();
    if (!short || short === currentLang) return;
    i18n.changeLanguage(short);
  };

  const LANGUAGE_MENU = LANGUAGES.map((l) => ({
    label: l.label,
    onClick: () => setLang(l.code),
    checked: l.code === currentLang,
  }));

  const hasAnyRole = (wanted = []) => wanted.some((r) => roles?.includes(r));
  const isAuth = !!currentUser;

  // retire séparateurs en doublon / en bord
  const compactMenu = (items) => {
    const out = [];
    for (const it of items) {
      if (it === "divider") {
        if (out.length === 0 || out[out.length - 1] === "divider") continue;
        out.push(it);
      } else {
        out.push(it);
      }
    }
    // retire divider final
    if (out[out.length - 1] === "divider") out.pop();
    return out;
  };

  // LEFT ITEMS traduits
  const LEFT_ITEMS_I18N = useMemo(
    () => [
      {
        id: "acheter",
        label: t("navbar.leftItems.acheter"),
        href: "/boutique",
      },
      {
        id: "events",
        label: t("navbar.leftItems.events"),
        href: "#",
        children: [
          {
            label: t("navbar.leftItems.events_create"),
            href: "/creer-evenement",
          },
          {
            label: t("navbar.leftItems.events_upcoming"),
            href: "/evenements-a-venir",
          },
        ],
      },
      {
        id: "biz",
        label: t("navbar.leftItems.biz"),
        href: "#",
        children: [
          { label: t("navbar.leftItems.biz_all"), href: "/entreprises" },

          {
            label: t("navbar.leftItems.biz_partner"),
            href: "/devenir-partenaire",
          },
          {
            label: t("navbar.leftItems.biz_findRep"),
            href: "/trouver-un-representant",
          },
        ],
      },
      {
        id: "explorer",
        label: t("navbar.leftItems.explorer"),
        href: "#",
        children: [
          { label: t("navbar.leftItems.about"), href: "/a-propos" },
          { label: t("navbar.leftItems.contact"), href: "/contact" },
          { label: t("navbar.leftItems.terms"), href: "/terms-and-conditions" },
          { label: t("navbar.leftItems.privacy"), href: "/privacy" },
          { label: t("navbar.leftItems.faq"), href: "/faq" },
        ],
      },
      {
        id: "news",
        label: t("menu.news"),
        href: "/actualites",
      },
    ],
    [t]
  );

  const handleCustomerPortal = async () => {
    if (!currentUser || loadingPortal) return;
    try {
      setLoadingPortal(true);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/Stripe/customer-portal`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ email: currentUser.email }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || t("navbar.alerts.portalAccessError")
        );
      }

      const data = await response.json();
      if (!data.url) {
        alert(t("navbar.alerts.noSubscription"));
        return;
      }

      window.location.href = data.url;
    } catch (error) {
      console.error(t("navbar.alerts.redirectErrorPrefix"), error);
      alert(error.message || t("navbar.alerts.genericError"));
    } finally {
      setLoadingPortal(false);
    }
  };

  const handleLogout = async () => {
    try {
      if (logout) await logout();
      else if (signOut) await signOut();
      navigate("/login"); // ou "/" si tu préfères
    } catch (e) {
      console.error(e);
      alert(
        t("navbar.alerts.logoutError") ||
          "Déconnexion impossible pour le moment."
      );
    }
  };
  console.log(permissions);

  const ACCOUNT_MENU = useMemo(() => {
    // Définition brute avec conditions d'accès
    const ALL = [
      // Visible seulement si connecté
      isAuth && {
        label: roles?.includes("company_owner")
          ? t("navbar.account.companiesManager") // ou t("navbar.editCompany") si tu préfères
          : t("navbar.account.companiesManager"),
        href: "/mon-compte/compagnies",
        requireAuth: true,
      },
      isAuth && {
        label: loadingPortal
          ? t("navbar.account.subscriptionsLoading")
          : t("navbar.account.subscriptions"),
        onClick: handleCustomerPortal,
        requireAuth: true,
      },

      isAuth && "divider",

      // Admin
      isAuth && {
        label: t("navbar.account.admin"),
        href: "/admin-dashboard",
        roles: ["admin"],
        requireAuth: true,
      },
      isAuth &&
        hasManagementAccess && {
          label: t("navbar.account.management"),
          href: "/management-dashboard",
          requireAuth: true,
        },

      // Directeur des ventes
      isAuth && {
        label: t("navbar.account.salesDirector"),
        href: "/directeur-ventes/representatives-list",
        roles: ["admin", "selling_director"],
        requireAuth: true,
      },

      // Représentant
      isAuth && {
        label: t("navbar.account.representative"),
        href: "/representative-dashboard",
        roles: ["admin", "representative"],
        requireAuth: true,
      },

      // Provider
      isAuth && {
        label: t("navbar.account.provider"),
        href: "/provider-dashboard",
        roles: ["provider", "admin"],
        requireAuth: true,
      },

      isAuth && {
        label: t("navbar.account.knownIssues"),
        href: "/knownissues",
        roles: ["representative", "admin"],
        requireAuth: true,
      },

      isAuth && "divider",

      // Logout (connecté)
      isAuth && {
        label: t("navbar.account.logout"),
        onClick: handleLogout, // <-- au lieu de href: "/logout"
        danger: true,
        requireAuth: true,
      },

      // Login (non connecté)
      !isAuth && { label: t("navbar.login"), href: "/login" },
    ].filter(Boolean);

    // Garde seulement les items autorisés
    const filtered = ALL.filter((it) => {
      if (it === "divider") return true;
      if (it.requireAuth && !isAuth) return false;
      if (it.roles && !hasAnyRole(it.roles)) return false;
      return true;
    });

    return compactMenu(filtered);
  }, [t, loadingPortal, roles, isAuth]);

  function DropdownPanel({ items, align = "left" }) {
    return (
      <div
        className={`invisible absolute ${
          align === "right" ? "right-0" : "left-0"
        } top-full z-50 w-72 translate-y-1 rounded-xl bg-white/95 opacity-0 shadow-lg backdrop-blur transition overflow-hidden p-0 group-hover/drop:visible group-hover/drop:translate-y-0 group-hover/drop:opacity-100`}
      >
        {items.map((it, idx) =>
          it === "divider" ? (
            <div key={`div-${idx}`} className="h-px bg-gray-200" />
          ) : it.onClick ? (
            <button
              key={it.label}
              type="button"
              onClick={it.onClick}
              className={`flex items-center justify-between w-full px-4 py-2 text-sm ${
                it.danger ? "text-red-600" : "text-gray-700"
              } hover:bg-[#F3DA77]/70 hover:text-gray-900`}
            >
              <span>{it.label}</span>
              {it.checked && <CheckIcon className="h-4 w-4 text-gray-600" />}
            </button>
          ) : (
            <a
              key={it.label}
              href={it.href}
              className={`flex items-center justify-between px-4 py-2 text-sm ${
                it.danger ? "text-red-600" : "text-gray-700"
              } hover:bg-[#F3DA77]/70 hover:text-gray-900`}
            >
              <span>{it.label}</span>
              {it.checked && <CheckIcon className="h-4 w-4 text-gray-600" />}
            </a>
          )
        )}
      </div>
    );
  }

  function NavItem({ label, href = "#", items = null, align = "left" }) {
    return (
      <div className="relative mx-1 group/drop before:absolute before:top-full before:left-0 before:h-3 before:w-full before:content-[''] before:pointer-events-none">
        {items ? (
          <>
            <button
              type="button"
              className="px-3 py-2 text-sm font-medium text-[#3a3a3a] hover:bg-white/60 inline-flex items-center gap-1 hover:rounded-full"
              aria-haspopup="menu"
              aria-expanded="false"
            >
              {label}
              <ChevronDownIcon className="h-4 w-4" />
            </button>
            <DropdownPanel items={items} align={align} />
          </>
        ) : (
          <a
            href={href}
            className="px-3 py-2 text-sm font-medium text-[#3a3a3a] hover:bg-white/60 hover:rounded-full"
          >
            {label}
          </a>
        )}
      </div>
    );
  }

  return (
    <header className="fixed inset-x-0 top-0 z-50">
      <div
        className="w-full border-b border-black/10 backdrop-blur-sm shadow-sm"
        style={{ backgroundColor: "rgba(255, 243, 175, 0.3)" }}
      >
        <nav className="flex h-12 md:h-14 items-center justify-between px-2 sm:px-4">
          {/* Logo + nav */}
          <div className="flex items-center gap-2">
            <a href="/" className="flex items-center gap-2">
              <img
                src={AnanasLogo}
                alt={t("navbar.logoAlt")}
                className="h-12 w-auto"
              />
            </a>

            <div className="hidden md:flex items-center">
              {LEFT_ITEMS_I18N.map((item) =>
                item.children ? (
                  <NavItem
                    key={item.id}
                    label={item.label}
                    items={item.children}
                    align="left"
                  />
                ) : (
                  <NavItem key={item.id} label={item.label} href={item.href} />
                )
              )}
            </div>
          </div>

          {/* Right desktop */}
          <div className="hidden md:flex items-center">
            <button
              type="button"
              onClick={() => navigate("/cart-store")}
              className="mr-1 inline-flex items-center justify-center rounded-full   p-2 text-[#3a3a3a] hover:bg-white"
              aria-label={t("navbar.cart", "Panier")}
              title={t("navbar.cart", "Panier")}
            >
              <ShoppingCartIcon className="h-5 w-5" />
            </button>
            <NavItem
              label={currentLang.toUpperCase()}
              items={LANGUAGE_MENU}
              align="right"
            />

            {isAuth ? (
              <NavItem
                label={t("navbar.myAccount")}
                items={ACCOUNT_MENU}
                align="right"
              />
            ) : (
              <a
                href="/login"
                className="px-3 py-2 text-sm font-medium text-[#3a3a3a] hover:bg-white/60 hover:rounded-full"
              >
                {t("navbar.loginCta")}
              </a>
            )}
          </div>

          {/* Mobile button */}
          <button
            className="md:hidden inline-flex items-center justify-center rounded-full border border-black/10 bg-white/70 p-2 text-[#3a3a3a] hover:bg-white"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label={t("navbar.menu")}
          >
            {mobileOpen ? (
              <XMarkIcon className="h-6 w-6" />
            ) : (
              <Bars3Icon className="h-6 w-6" />
            )}
          </button>
        </nav>

        {/* Drawer mobile */}
        {mobileOpen &&
          createPortal(
            <div className="fixed inset-0 z-[9999] flex">
              <div
                className="absolute inset-0 bg-black/40 backdrop-blur-sm"
                onClick={() => {
                  setMobileOpen(false);
                  setSubmenu(null);
                }}
              />
              <div className="relative h-full w-[85%] max-w-[420px] bg-white shadow-2xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 border-b border-black/10 bg-gradient-to-r from-[#eaf6e4] to-[#fff6d3]">
                  <a
                    href="/"
                    onClick={() => setMobileOpen(false)}
                    className="flex items-center gap-2"
                  >
                    <img
                      src={AnanasLogo}
                      alt={t("navbar.logoAlt")}
                      className="h-9 w-auto"
                    />
                  </a>
                  <button
                    onClick={() => setMobileOpen(false)}
                    className="h-9 w-9 grid place-items-center rounded-full bg-white/80 shadow"
                    aria-label={t("navbar.menu")}
                  >
                    <XMarkIcon className="h-5 w-5 text-[#3a3a3a]" />
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto px-2 py-3">
                  {!submenu && (
                    <div className="overflow-hidden bg-white [&>*+*]:border-t [&>*+*]:border-black/10">
                      {LEFT_ITEMS_I18N.map((item) =>
                        item.children ? (
                          <button
                            key={item.id}
                            onClick={() =>
                              setSubmenu({
                                title: item.label,
                                items: item.children,
                              })
                            }
                            className="w-full flex items-center justify-between px-4 py-3 text-[15px] text-[#3a3a3a] hover:bg-gray-50"
                          >
                            <span>{item.label}</span>
                            <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                          </button>
                        ) : (
                          <a
                            key={item.id}
                            href={item.href}
                            onClick={() => setMobileOpen(false)}
                            className="block px-4 py-3 text-[15px] text-[#3a3a3a] hover:bg-gray-50"
                          >
                            {item.label}
                          </a>
                        )
                      )}

                      {/* Language */}

                      <button
                        onClick={() =>
                          setSubmenu({
                            title: t("navbar.language"),
                            items: LANGUAGE_MENU,
                          })
                        }
                        className="w-full flex items-center justify-between px-4 py-3 text-[15px] text-[#3a3a3a] hover:bg-gray-50"
                      >
                        <span className="inline-flex items-center gap-2">
                          {currentLang.toUpperCase()}
                        </span>
                        <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                      </button>

                      {!isAuth && (
                        <a
                          href="/login"
                          onClick={() => {
                            setMobileOpen(false);
                            setSubmenu(null);
                          }}
                          className="block px-4 py-3 text-[15px] text-[#3a3a3a] hover:bg-gray-50"
                        >
                          {t("navbar.loginCta")}
                        </a>
                      )}

                      {/* Account */}
                      {isAuth && (
                        <button
                          onClick={() =>
                            setSubmenu({
                              title: t("navbar.myAccount"),
                              items: ACCOUNT_MENU.filter(
                                (i) => i !== "divider"
                              ),
                            })
                          }
                          className="w-full flex items-center justify-between px-4 py-3 text-[15px] text-[#3a3a3a] hover:bg-gray-50"
                        >
                          <span className="inline-flex items-center gap-2">
                            <UserCircleIcon className="h-5 w-5" />
                            {t("navbar.manageMyAccount")}
                          </span>
                          <ChevronRightIcon className="h-5 w-5 text-gray-400" />
                        </button>
                      )}
                    </div>
                  )}

                  {submenu && (
                    <>
                      <button
                        onClick={() => setSubmenu(null)}
                        className="inline-flex items-center gap-1 mb-3 px-3 py-2 text-sm font-medium text-[#3a3a3a] hover:bg-gray-50"
                      >
                        <ChevronRightIcon className="h-4 w-4 rotate-180" />
                        {t("navbar.back")}
                      </button>

                      <div className="px-3 py-1 text-sm font-semibold text-[#3a3a3a]/80">
                        {submenu.title}
                      </div>

                      <nav className="mt-2 overflow-hidden bg-white [&>*+*]:border-t [&>*+*]:border-black/10">
                        {submenu.items.map((it, idx) =>
                          it.onClick ? (
                            <button
                              key={`${it.label}-${idx}`}
                              type="button"
                              onClick={async () => {
                                await it.onClick();
                                setSubmenu(null);
                                setMobileOpen(false);
                              }}
                              className={`flex w-full items-center justify-between px-4 py-3 text-left text-[15px] hover:bg-gray-50 ${
                                it.danger ? "text-red-600" : "text-[#3a3a3a]"
                              }`}
                            >
                              <span>{it.label}</span>
                              <ChevronRightIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            </button>
                          ) : (
                            <a
                              key={`${it.label}-${idx}`}
                              href={it.href}
                              onClick={() => {
                                setSubmenu(null);
                                setMobileOpen(false);
                              }}
                              className={`flex w-full items-center justify-between px-4 py-3 text-[15px] hover:bg-gray-50 ${
                                it.danger ? "text-red-600" : "text-[#3a3a3a]"
                              }`}
                            >
                              <span>{it.label}</span>
                              <ChevronRightIcon
                                className="h-5 w-5 text-gray-400"
                                aria-hidden="true"
                              />
                            </a>
                          )
                        )}
                      </nav>
                    </>
                  )}
                </div>
              </div>
            </div>,
            document.body
          )}
      </div>
    </header>
  );
}
